import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle, Warning, XCircle } from "@phosphor-icons/react";
import { usePlatform } from "@/context/PlatformContext";

// Public status page. Renders ONLY when the super_admin has opted in via
// /settings/platform → "Show public status page". Server returns 404 otherwise.
// Un-authenticated — safe to link from your marketing site or a "status" link
// in the footer.

function uptimeBadge(pct) {
  if (pct >= 99.9) return { Icon: CheckCircle, text: "All systems operational", tone: "emerald" };
  if (pct >= 98)   return { Icon: Warning,     text: "Minor delivery blips",     tone: "amber" };
  return                { Icon: XCircle,      text: "Delivery incidents",       tone: "red" };
}

export default function Status() {
  const platform = usePlatform();
  const [data, setData] = useState(null);
  const [notPublished, setNotPublished] = useState(false);

  useEffect(() => {
    api.get("/status")
      .then((r) => setData(r.data))
      .catch((err) => {
        // 404 means the operator hasn't published — show a friendly "hidden" screen.
        if (err.response?.status === 404) setNotPublished(true);
      });
  }, []);

  if (notPublished) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center max-w-md p-8">
          <div className="label-mono mb-3">Status page</div>
          <div className="font-chivo font-black text-3xl uppercase tracking-tighter mb-2">Not published</div>
          <div className="text-sm text-zinc-600">
            The operator has not enabled the public status page yet. If you&apos;re an admin, flip the toggle
            on <Link to="/settings/platform" className="underline hover:text-black">/settings/platform</Link>.
          </div>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="label-mono">Loading…</div>
      </div>
    );
  }

  const { platform: p, leads, delivery } = data;
  const badge = uptimeBadge(delivery.uptime_24h_pct);
  const BadgeIcon = badge.Icon;
  const toneClasses = {
    emerald: "text-emerald-700 border-emerald-700 bg-emerald-50",
    amber:   "text-amber-700   border-amber-700   bg-amber-50",
    red:     "text-red-700     border-red-700     bg-red-50",
  }[badge.tone];

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-5xl mx-auto p-8">

        {/* Header */}
        <div className="flex items-end justify-between mb-8 border-b-4 border-black pb-6">
          <div>
            <div className="label-mono">{p.tagline}</div>
            <div className="font-chivo font-black text-5xl uppercase tracking-tighter" data-testid="status-platform-name">
              {p.short_name || p.name}
            </div>
            <div className="mt-1 text-sm text-zinc-600">{p.domain}</div>
          </div>
          {p.support_email && (
            <a href={`mailto:${p.support_email}`} className="border-2 border-black px-3 py-2 text-xs uppercase tracking-widest hover:bg-black hover:text-white"
              data-testid="status-support-email">
              Contact support
            </a>
          )}
        </div>

        {/* Big status badge */}
        <div className={`brut-card p-8 mb-6 border-4 ${toneClasses}`} data-testid="status-badge">
          <div className="flex items-center gap-4">
            <BadgeIcon size={48} weight="bold" />
            <div>
              <div className="label-mono">System status</div>
              <div className="font-chivo font-black text-3xl uppercase tracking-tighter">
                {data.headline || badge.text}
              </div>
              {data.subhead && <div className="text-sm mt-1">{data.subhead}</div>}
            </div>
          </div>
        </div>

        {/* KPIs — Delivery uptime + volume */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <div className="brut-card p-6" data-testid="status-uptime">
            <div className="label-mono">Delivery uptime · 24h</div>
            <div className="font-chivo font-black text-4xl mt-1">
              {delivery.uptime_24h_pct.toFixed(2)}%
            </div>
            <div className="text-xs text-zinc-600 mt-1 font-mono">
              {delivery.success_24h.toLocaleString()} / {delivery.attempts_24h.toLocaleString()} attempts
            </div>
          </div>
          <div className="brut-card p-6" data-testid="status-today">
            <div className="label-mono">Leads processed · today</div>
            <div className="font-chivo font-black text-4xl mt-1">
              {leads.today.received.toLocaleString()}
            </div>
            <div className="text-xs text-zinc-600 mt-1 font-mono">
              {leads.today.delivered.toLocaleString()} delivered · {leads.today.capped.toLocaleString()} capped
            </div>
          </div>
          <div className="brut-card p-6" data-testid="status-week">
            <div className="label-mono">Leads processed · last 7d</div>
            <div className="font-chivo font-black text-4xl mt-1">
              {leads.week.received.toLocaleString()}
            </div>
            <div className="text-xs text-zinc-600 mt-1 font-mono">
              {leads.week.delivered.toLocaleString()} delivered · {leads.week.capped.toLocaleString()} capped
            </div>
          </div>
        </div>

        <div className="text-[11px] text-zinc-500 font-mono">
          Generated {new Date(data.generated_at).toLocaleString()} — refresh anytime for the latest snapshot.
        </div>
      </div>
    </div>
  );
}
